(function () {
  /**
   * Thin fetch wrapper for commerce-worker (the same backend the Flutter app uses —
   * see cloudflare/commerce-worker/. Contract verified against its route source,
   * not guessed: docs/decisions/ADR-174-web-native-commerce-account-checkout.md).
   */

  function baseUrl() {
    var vars = window.themeVariables || {};
    var settings = vars.settings || {};
    // Default to the first-party api.erosstore.co subdomain (same registrable
    // domain as the storefront) so the worker can set/read an HttpOnly session
    // cookie for the web (ADR httpOnly session migration). The *.workers.dev
    // origin still works and is what native (Flutter) clients use.
    return (settings.erosCommerceApiBase || 'https://api.erosstore.co').replace(/\/+$/, '');
  }

  function ErosCommerceError(message, status, code, details) {
    this.name = 'ErosCommerceError';
    this.message = message;
    this.status = status;
    this.code = code;
    this.details = details;
  }
  ErosCommerceError.prototype = Object.create(Error.prototype);

  /**
   * @param {string} path e.g. '/v1/auth/otp/send'
   * @param {object} [opts]
   * @param {string} [opts.method='GET']
   * @param {object} [opts.body]
   * @param {boolean} [opts.auth=false] require a signed-in session (auth travels via the HttpOnly cookie)
   * @param {FormData} [opts.formData] send as multipart instead of JSON
   */
  function request(path, opts) {
    opts = opts || {};
    var method = opts.method || 'GET';
    var headers = {};
    var body;

    if (opts.formData) {
      body = opts.formData; // browser sets multipart Content-Type + boundary
    } else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }

    if (opts.auth) {
      if (!window.ErosSession || !window.ErosSession.isSignedIn()) {
        return Promise.reject(new ErosCommerceError('not_signed_in', 401, 'not_signed_in'));
      }
      // No Authorization header — the JWT is never held in JS-readable
      // storage. credentials:'include' (below) carries the first-party
      // HttpOnly `eros_session` cookie instead.
    }

    // credentials:'include' sends/receives the first-party HttpOnly session
    // cookie on api.erosstore.co — the only transport for auth now.
    return fetch(baseUrl() + path, { method: method, headers: headers, body: body, credentials: 'include' })
      .catch(function () {
        throw new ErosCommerceError('network_error', 0, 'network_error');
      })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            if (!res.ok || data.ok === false) {
              var code = data.error || data.code || 'request_failed';
              if (opts.auth && res.status === 401) sessionEnded(code);
              throw new ErosCommerceError(code, res.status, code, data);
            }
            return data;
          });
      });
  }

  /**
   * The worker refused the session cookie on an authenticated call (expired,
   * or signed with a rotated secret — the 2026-09-25 rotation's grace ends
   * 2026-10-26). Mirrors the app (2026-09-30): sign out locally so every page
   * asks for sign-in again, and say why once — instead of silent failures
   * while the page still believes the shopper is signed in.
   */
  function sessionEnded(code) {
    // ADR-199: this account was merged into another one (soft merge from
    // «أرقامي» on another device). Its session is dead for good — sign out
    // the same way the hub's sign-out button does, and say why.
    var merged = code === 'account_merged';
    if (!merged && code !== 'invalid_token' && code !== 'unauthorized') return Promise.resolve(false);
    if (!window.ErosSession || !window.ErosSession.isSignedIn()) return Promise.resolve(false);
    if (merged) {
      endSessionLocally(true);
      return Promise.resolve(true);
    }
    // ADR-306 (owner 2026-10-29): only the customer's own logout ends a session. A refusal
    // renews first; only commerce's definitive answer (the token proves on no secret, or the
    // browser holds no cookie at all) signs out. A blip (network, 5xx) keeps the session.
    return renewSession().then(function (r) {
      if (r !== 'invalid_token' && r !== 'unauthorized') return false;
      endSessionLocally(false);
      return true;
    });
  }

  /** ADR-306: POST /v1/auth/session/renew with the cookie → 'renewed' | 'invalid_token' | 'unauthorized' | 'kept'. */
  var RENEWED_AT_KEY = 'eros_session_renewed_at_v1';
  var RENEW_EVERY_MS = 24 * 60 * 60 * 1000;
  function renewSession() {
    return fetch(baseUrl() + '/v1/auth/session/renew', { method: 'POST', credentials: 'include' })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (res.status === 200 && data.renewed) {
            try { window.localStorage.setItem(RENEWED_AT_KEY, String(Date.now())); } catch (e) { /* private mode */ }
            return 'renewed';
          }
          return res.status === 401 && (data.error === 'invalid_token' || data.error === 'unauthorized') ? data.error : 'kept';
        });
      })
      .catch(function () { return 'kept'; });
  }

  /** Once a day on page load: the cookie and its token come back for another 400 days (and Safari's cap restarts). */
  function renewIfDue() {
    if (!window.ErosSession || !window.ErosSession.isSignedIn()) return;
    var last = 0;
    try { last = Number(window.localStorage.getItem(RENEWED_AT_KEY) || 0); } catch (e) { last = 0; }
    if (Date.now() - last < RENEW_EVERY_MS) return;
    renewSession().then(function (r) {
      if (r === 'invalid_token' || r === 'unauthorized') endSessionLocally(false);
    });
  }

  function endSessionLocally(merged) {
    if (merged) {
      if (window.ErosWebPush && window.ErosWebPush.forget) {
        try { window.ErosWebPush.forget(); } catch (e) { /* best effort */ }
      }
      try { request('/v1/auth/logout', { method: 'POST' }).catch(function () {}); } catch (e) { /* best effort */ }
    }
    // The account pages show the reason above the sign-in panel (a toast is
    // gone before the page settles) — remembered for this tab only.
    if (merged) {
      try { window.sessionStorage.setItem('eros_signed_out_reason', 'account_merged'); } catch (e) { /* private mode */ }
    }
    window.ErosSession.clear();
    try { window.localStorage.removeItem(RENEWED_AT_KEY); } catch (e) { /* private mode */ }
    var strings = window.ErosAccountStrings || {};
    var auth = strings.auth || {};
    var phones = strings.phones || {};
    if (window.ErosUI && window.ErosUI.toast) {
      window.ErosUI.toast(
        merged
          ? phones.account_merged || 'انضمّ حسابك إلى حساب آخر. سجّل الدخول من جديد برقمك.'
          : auth.session_expired || 'انتهت جلستك، سجّل دخولك من جديد',
        'error'
      );
    }
  }

  /**
   * ADR-200: GET /v1/gift-credit/config, shared by the cart rule, the
   * checkout and the wallet. Kept for 5 minutes in this tab (memory +
   * sessionStorage) so adding to the cart does not wait on it every time.
   * A failed fetch resolves to "off, no gift variants" and is not cached —
   * the server refuses a wrong gift checkout anyway (it is the authority).
   */
  var GIFT_CONFIG_KEY = 'eros_gift_credit_config_v1';
  var GIFT_CONFIG_TTL_MS = 5 * 60 * 1000;
  var giftConfigMemo = null;
  var giftConfigInFlight = null;

  function normaliseGiftConfig(data) {
    data = data || {};
    return {
      enabled: data.enabled === true,
      products: Array.isArray(data.products)
        ? data.products.filter(function (p) {
            return p && typeof p.variantGid === 'string';
          })
        : [],
      paymentChipIds: Array.isArray(data.paymentChipIds) ? data.paymentChipIds.map(String) : [],
      productHandle: typeof data.productHandle === 'string' && data.productHandle ? data.productHandle : null,
    };
  }

  function giftCreditConfigCached() {
    var now = Date.now();
    if (giftConfigMemo && now - giftConfigMemo.at < GIFT_CONFIG_TTL_MS) return Promise.resolve(giftConfigMemo.value);
    try {
      var stored = JSON.parse(window.sessionStorage.getItem(GIFT_CONFIG_KEY) || 'null');
      if (stored && stored.value && now - stored.at < GIFT_CONFIG_TTL_MS) {
        giftConfigMemo = stored;
        return Promise.resolve(stored.value);
      }
    } catch (e) {
      /* storage blocked — fetch below */
    }
    if (giftConfigInFlight) return giftConfigInFlight;
    giftConfigInFlight = request('/v1/gift-credit/config')
      .then(function (data) {
        var value = normaliseGiftConfig(data);
        giftConfigMemo = { at: Date.now(), value: value };
        try {
          window.sessionStorage.setItem(GIFT_CONFIG_KEY, JSON.stringify(giftConfigMemo));
        } catch (e) {
          /* storage blocked */
        }
        return value;
      })
      .catch(function () {
        return normaliseGiftConfig(null);
      })
      .then(function (value) {
        giftConfigInFlight = null;
        return value;
      });
    return giftConfigInFlight;
  }

  if (typeof setTimeout === "function") setTimeout(renewIfDue, 0);

  window.ErosCommerce = {
    // ADR-306: a refusal on another worker (reviews) asks here; resolves true only when the session truly ended.
    sessionEnded: sessionEnded,
    Error: ErosCommerceError,
    baseUrl: baseUrl,
    request: request,

    // --- auth ---
    otpSend: function (phone) {
      return request('/v1/auth/otp/send', { method: 'POST', body: { phone: phone } });
    },
    // ADR-320: `cart` ([{variantGid, quantity}], ≤ 50) is kept by the server only if the sign-in struggles,
    // so staff can call the customer about it.
    otpVerify: function (phone, code, signupToken, cart) {
      var body = { phone: phone, code: code };
      if (signupToken) body.signupToken = signupToken;
      if (Array.isArray(cart) && cart.length) body.cart = cart.slice(0, 50);
      return request('/v1/auth/otp/verify', { method: 'POST', body: body });
    },
    register: function (phone, firstName, gender, birthday) {
      return request('/v1/auth/register', {
        method: 'POST',
        body: { phone: phone, firstName: firstName, gender: gender, birthday: birthday },
      });
    },
    phoneCheck: function (phone) {
      return request('/v1/auth/phone/check', { method: 'POST', body: { phone: phone } });
    },
    session: function () {
      return request('/v1/auth/session', { auth: true });
    },
    // Clears the server-side HttpOnly session cookie on sign-out. No auth
    // required (it just expires the cookie); credentials:'include' carries the
    // cookie so the worker can clear it. Best-effort — the caller clears local
    // state regardless of this resolving.
    logout: function () {
      return request('/v1/auth/logout', { method: 'POST' });
    },
    // ADR-319: «تسجيل الخروج من كل الأجهزة» — every session of this account ends, this one included.
    logoutAll: function () {
      return request('/v1/auth/logout/all', { method: 'POST', auth: true });
    },

    // --- profile ---
    getProfile: function () {
      return request('/v1/customer/profile', { auth: true });
    },
    updateProfile: function (patch) {
      return request('/v1/customer/profile', { method: 'PATCH', auth: true, body: patch });
    },
    /** ADR-201: the cropped 800×676 JPEG from eros-account-photo-crop.js → { profilePhotoUrl }. */
    uploadProfilePhoto: function (blob) {
      var formData = new FormData();
      formData.append('image', blob, 'profile.jpg');
      return request('/v1/customer/profile-photo', { method: 'POST', auth: true, formData: formData });
    },

    // --- addresses ---
    listAddresses: function () {
      return request('/v1/customer/addresses', { auth: true });
    },
    createAddress: function (address) {
      return request('/v1/customer/addresses', { method: 'POST', auth: true, body: address });
    },
    updateAddress: function (id, address) {
      return request('/v1/customer/addresses/update', {
        method: 'POST',
        auth: true,
        body: Object.assign({ id: id }, address),
      });
    },
    deleteAddress: function (id) {
      return request('/v1/customer/addresses/delete', { method: 'POST', auth: true, body: { id: id } });
    },
    setDefaultAddress: function (id) {
      return request('/v1/customer/addresses/default', { method: 'POST', auth: true, body: { id: id } });
    },

    // --- linked phone numbers «أرقامي» (ADR-199) ---
    // The other number's owner receives a WhatsApp code; verifying it returns
    // a short-lived `proof` that the fetch/link calls spend.
    listPhones: function () {
      return request('/v1/customer/phones', { auth: true });
    },
    /** purpose: 'fetch' | 'link' */
    phoneCode: function (phone, purpose) {
      return request('/v1/customer/phones/code', {
        method: 'POST',
        auth: true,
        body: { phone: phone, purpose: purpose },
      });
    },
    phoneVerify: function (phone, code, purpose) {
      return request('/v1/customer/phones/verify', {
        method: 'POST',
        auth: true,
        body: { phone: phone, code: code, purpose: purpose },
      });
    },
    fetchOrdersFromPhone: function (proof, orderGids) {
      return request('/v1/customer/phones/fetch-orders', {
        method: 'POST',
        auth: true,
        body: { proof: proof, orderGids: orderGids },
      });
    },
    linkPhone: function (proof) {
      return request('/v1/customer/phones/link', { method: 'POST', auth: true, body: { proof: proof } });
    },
    unlinkPhone: function (phone) {
      return request('/v1/customer/phones/unlink', { method: 'POST', auth: true, body: { phone: phone } });
    },

    // --- wallet + gift credit «بطاقة رصيد إيروس» (ADR-200) ---
    // commerce-worker src/routes/gift_credit.ts. The config is public (no
    // auth): it says which variants are gift credit, which payment chips a
    // gift cart may use, and the product handle the wallet links to.
    giftCreditConfig: function () {
      return giftCreditConfigCached();
    },
    wallet: function () {
      return request('/v1/wallet', { auth: true });
    },
    redeemGiftCode: function (code) {
      return request('/v1/wallet/redeem', { method: 'POST', auth: true, body: { code: code } });
    },
    myGiftCodes: function () {
      return request('/v1/customer/gift-codes', { auth: true });
    },
    revealGiftCode: function (id) {
      return request('/v1/customer/gift-codes/reveal', { method: 'POST', auth: true, body: { id: id } });
    },

    // --- checkout ---
    paymentGateways: function () {
      return request('/v1/shop/payment-gateways');
    },
    // GET, no auth. Live title/price for the two real Shopify delivery
    // rates behind the checkout's delivery-vs-gift toggle ("تغليف قياسي" /
    // "تغليف كهدية") — the owner changes these prices himself in Shopify
    // Admin, no code deploy, so this is always fetched fresh rather than
    // hardcoded. Per the shared contract this endpoint always resolves
    // 200 (best-effort/cached on its own failures) — eros-checkout.js still
    // has its own hardcoded 3000/6000 fallback for the network-error case
    // (request() rejecting before a response is even received).
    shippingFees: function () {
      return request('/v1/checkout/shipping-fees');
    },
    // Shopify's own pricing of this cart for THIS customer — the birthday
    // discount the worker will put on the order (ADR-170). The theme's
    // Liquid prices cannot know a customer's birthday, so the sheet asks.
    // ADR-213: `offerToken` (the personal offer link's token) makes the quote
    // carry an `offer` object — per-line discounts, or why it does not apply.
    // ADR-225: `attribution` ({ref, at} from ErosAttribution.current()) rides along only when given.
    checkoutQuote: function (lines, fulfillmentMethod, isGift, discountCode, offerToken, attribution) {
      var body = { lines: lines, fulfillmentMethod: fulfillmentMethod, isGift: !!isGift };
      if (discountCode) body.discountCode = discountCode;
      if (offerToken) body.offerToken = offerToken;
      if (attribution) body.attribution = attribution;
      return request('/v1/checkout/quote', { method: 'POST', auth: true, body: body });
    },
    // ADR-213: GET /v1/offers/preview?token= — the session cookie rides along
    // when signed in; a signed-out visitor gets state 'sign_in'.
    offerPreview: function (token) {
      return request('/v1/offers/preview?token=' + encodeURIComponent(token));
    },
    confirmCheckout: function (payload) {
      // `eros_platform` on the order (commerce-worker contract 2026-09-10):
      // the store site always declares 'web', so an order from a phone
      // browser is never mistaken for one from the app. The worker would
      // otherwise sniff the User-Agent and call this one 'android'.
      var body = Object.assign({}, payload, { clientPlatform: 'web' });
      return request('/v1/checkout/confirm', { method: 'POST', auth: true, body: body });
    },
    // Reservation-aware ceilings for cart lines — Shopify's quantity minus
    // units held by OTHER customers' live 10-minute card holds, which the
    // Storefront/AJAX cart knows nothing about. Signed-in only. A variant is
    // ABSENT from `units` when it has no real ceiling (untracked inventory /
    // CONTINUE oversell); callers must leave those lines alone, not zero them.
    variantAvailability: function (variantGids) {
      return request('/v1/checkout/availability', {
        method: 'POST',
        auth: true,
        body: { variantGids: variantGids },
      });
    },
    // Polling target for an AlQaseh card checkout BEFORE any Shopify order
    // exists (order creation is deferred until AlQaseh confirms payment —
    // see cloudflare/commerce-worker's checkout_card_pending.ts). Only
    // present when confirmCheckout's response carries a `checkoutId`.
    getPendingCheckout: function (checkoutId) {
      return request('/v1/checkout/pending/' + encodeURIComponent(checkoutId), { auth: true });
    },

    // --- orders ---
    listOrders: function () {
      return request('/v1/customer/orders', { auth: true });
    },
    getOrder: function (id) {
      return request('/v1/customer/orders/' + encodeURIComponent(id), { auth: true });
    },
    cancelOrder: function (orderGid, reasonCode, reasonText) {
      return request('/v1/orders/cancel', {
        method: 'POST',
        auth: true,
        body: { orderGid: orderGid, reasonCode: reasonCode, reasonText: reasonText },
      });
    },
    // ADR-301: the refusal of a customer whose account is tagged `block` — the
    // server's text in the page language (Arabic until Kurdish is signed) and
    // where «تواصل معنا» goes. Null for any other error.
    customerBlocked: function (err) {
      if (!err || err.code !== 'customer_blocked') return null;
      var d = err.details || {};
      var msgs = d.messages || {};
      var lang = String(document.documentElement.lang || 'ar').slice(0, 2);
      return { message: msgs[lang] || d.message || msgs.ar || '', contactUrl: d.contactUrl || '' };
    },
    customerBlockedLink: function (contactUrl, label) {
      var a = document.createElement('a');
      a.href = contactUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.className = 'button button--sm';
      a.setAttribute('data-eros-customer-blocked-contact', '');
      a.textContent = label;
      return a;
    },
    addOrderLines: function (orderGid, lines) {
      return request('/v1/orders/add-lines', { method: 'POST', auth: true, body: { orderGid: orderGid, lines: lines } });
    },
    submitReturn: function (returnLines, exchangeLines) {
      var body = { returnLines: returnLines };
      if (exchangeLines && exchangeLines.length) body.exchangeLines = exchangeLines;
      return request('/v1/orders/return-request', { method: 'POST', auth: true, body: body });
    },
    uploadReturnAttachment: function (file) {
      var formData = new FormData();
      formData.append('file', file);
      return request('/v1/orders/return-attachments', { method: 'POST', auth: true, formData: formData });
    },
  };
})();
